/**
 * SegmentedControl - Grouped button selection
 *
 * Usage:
 * <SegmentedControl
 *   label="Position"
 *   value={position}
 *   onChange={setPosition}
 *   options={[
 *     { value: 'left', label: 'Left' },
 *     { value: 'right', label: 'Right' },
 *   ]}
 * />
 */

import type { ReactNode } from 'react';

import { cn } from '../utils';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  /** Group label */
  label: string;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
  /** Available options */
  options: SegmentedControlOption<T>[];
  /** Selected value */
  value: T;
  /** Change handler */
  onChange: (value: T) => void;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Full width */
  fullWidth?: boolean;
  /** Error message */
  error?: string;
  /** Helper text */
  hint?: string;
  /** Disable entire control */
  disabled?: boolean;
  /** Additional classes for the container */
  className?: string;
}

const SIZE_CLASSES = {
  sm: {
    container: 'p-0.5',
    button: 'px-2.5 py-1 text-xs',
    icon: 'mr-1',
  },
  md: {
    container: 'p-0.5',
    button: 'px-3 py-1.5 text-sm',
    icon: 'mr-1.5',
  },
};

export function SegmentedControl<T extends string>({
  label,
  hideLabel,
  options,
  value,
  onChange,
  size = 'md',
  fullWidth,
  error,
  hint,
  disabled,
  className,
}: SegmentedControlProps<T>) {
  const sizes = SIZE_CLASSES[size];

  return (
    <fieldset className={cn(fullWidth && 'w-full', className)}>
      <legend
        className={cn(
          'mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300',
          hideLabel && 'sr-only'
        )}
      >
        {label}
      </legend>
      <div
        role="radiogroup"
        aria-invalid={error ? true : undefined}
        className={cn(
          'inline-flex rounded-lg bg-gray-100 dark:bg-zinc-800',
          sizes.container,
          fullWidth && 'w-full'
        )}
      >
        {options.map((option) => {
          const isActive = option.value === value;
          const isDisabled = disabled || option.disabled;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-disabled={isDisabled || undefined}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                onChange(option.value);
              }}
              className={cn(
                'rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100 dark:focus-visible:ring-offset-zinc-800',
                sizes.button,
                fullWidth && 'flex-1',
                isActive
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white',
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {option.icon ? (
                <span className={cn('inline-flex items-center', sizes.icon)}>{option.icon}</span>
              ) : null}
              {option.label}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
    </fieldset>
  );
}
